<template>
    <main
        id="wrapper"
        ref="wrapper"
        :data-browser-back-button="backButton"
        :data-step="store.state.page.step"
        :data-form-step="store.state.form.attributes.step"
        :data-question="hasQuiz && store.state.quiz.currentQuestion"
        :data-question-count="hasQuiz && store.state.quiz.questionCount"
        :data-advanced-flow="hasQuiz && store.state.quiz.advancedFlow"
        :data-equal-flow="hasQuiz && store.state.quiz.equalFlow"
        :data-total-flow-length="hasQuiz && store.state.quiz.totalFlowLength"
        :data-device="store.state.page.deviceType"
        :style="inlineStyle"
    >
        <zoe-organism-logobar
            v-if="showLogobar"
            :content="get('general.logobar')"
        />

        <zoe-molecule-alert-dialog
            v-if="useAlertDialog"
            :content="get('general.logobar.alertDialog')"
        />

        <zoe-organism-popup-notification
            v-if="showPopupNotification"
            :content="get(`general.popupNotification`)"
        />

        <div class="step-wrapper">
            <transition name="step">
                <component
                    :is="stepComponent(currentStep)"
                    v-if="currentStep.key"
                    :index="index + 1"
                    :step="currentStep.key"
                    :content="get(currentStep.key)"
                />
            </transition>
        </div>
    </main>
</template>

<script setup>
import { computed, defineAsyncComponent, reactive, watchEffect, onMounted } from 'vue';
import { useStore } from 'vuex';
import { get } from '../../../../../../resources/frontend/js/mixins/get-page-data';
import { generateButtonVariables, generateContainerVariables, generateInputVariables } from '@mixins/styling/styling';
import convertSize from '@mixins/utils/convert-size';
import generateAllFlows from '@mixins/quiz/v2.0.0/generate-all-flows';
import { getAllAnswers } from '@mixins/quiz/v2.0.0/get-all-answers';
import getCurrentStep from '@mixins/utils/get-current-step';
import showOnStep from '@mixins/utils/show-on-step';
import dataLayerPush from '@mixins/utils/data-layer-push';
import { handleFirstQuestion } from '@mixins/utils/first-question';

// json5-ignore
import ZoeOrganismLogobar from '../../logobar/v2.2.0/zoe-organism-logobar.vue';
// json5-ignore
import ZoeOrganismPopupNotification from '../../popup-notification/variation-1/v1.0.0/zoe-organism-popup-notification.vue';
import ZoeMoleculeAlertDialog from '@molecules/alert-dialog/zoe-molecule-alert-dialog.vue';

const store = useStore();

const { backButton, steps } = defineProps({
    backButton: {
        type: Boolean,
        default: true,
    },
    steps: {
        type: Array,
        default: () => [],
    },
});

onMounted(() => {
    if (store.state.page.step === 1) {
        dataLayerPush('Start');
    }
});

store.commit('page/setValue', { key: 'steps', value: steps.map(step => step) });
store.commit('page/setValue', { key: 'totalSteps', value: steps.length });

const currentStep = computed(() => {
    return getCurrentStep();
});

const showLogobar = computed(() => {
    return showOnStep(currentStep.value, 'general.logobar');
});

const useAlertDialog = computed(() => {
    return get('general.logobar.alertDialog.enable.value');
});

const showPopupNotification = computed(() => {
    return get('general.popupNotification') && showOnStep(currentStep.value, 'general.popupNotification');
});

const stepComponent = step => {
    return defineAsyncComponent(() => import(`../../../../../steps/${step.type}/${step.name}/${step.version}/main.vue`));
};

const inlineStyle = reactive({
    ...generateButtonVariables('*'),
    ...generateInputVariables('*'),
    ...generateContainerVariables('*'),
});

const logobarHeight = computed(() => store.getters['component/getOrganismValue']('logobar', 'height'));
const logobarPosition = computed(() => store.getters['component/getOrganismValue']('logobar', 'position'));

watchEffect(() => {
    if (logobarPosition.value === 'fixed' || logobarPosition.value === 'sticky') {
        inlineStyle['padding-top'] = convertSize(logobarHeight.value);
    }
});

const hasQuiz = store.state.quiz?.questionsData?.length > 0;

if (hasQuiz) {
    store.commit('quiz/setQuiz', { key: 'version', value: 'flex' });

    const answers = getAllAnswers();
    generateAllFlows(answers);

    handleFirstQuestion();
}
</script>

<style lang="scss">
@use '../../../../../resources/scss/partials/reset';
@use '../../../../../resources/scss/partials/animations';
@use '../../../../../resources/scss/partials/step';
@use '../../../../../resources/scss/partials/responsive';
@use '../../../../../resources/scss/partials/general-styling';
@use '../../../../../resources/scss/partials/form';
@use '../../../../../resources/scss/partials/default-components/info-popup/info-popup-1.1.0';
</style>
